import React from "react";
import { findTextDirectives } from "@/lib/MarkdownDirective";

// セキュリティ方針:
// - `<a>` を生成するのは URL が `new URL()` でパース可能かつ protocol が
//   http:/https: の場合、またはサイト内パス（`/` から始まり `//` ではない）の場合のみ。
// - それ以外（`javascript:` や `data:` など）は元の Markdown 文字列をプレーンテキストとして表示する。
// - 太字（`**text**`）・リンク・出典参照（`[^N]`）は互いにネストしない前提で、1つの正規表現でまとめて走査する。
// - テキストディレクティブ（`:name{k="v"}`）は呼び出し側が渡したレンダラーで描画し、レンダラーが無ければ元の文字列のまま表示する。

const INLINE_LINK_PATTERN = /\[([^[\]]*)\]\(([^()]*)\)/g;
const INLINE_BOLD_PATTERN = /\*\*([^*]+)\*\*/g;
const INLINE_SOURCE_REF_PATTERN = /\[\^(\d+)\]/g;
const INLINE_PATTERN = /\[([^[\]]*)\]\(([^()]*)\)|\*\*([^*]+)\*\*|\[\^(\d+)\]/g;

export interface InlineMarkdownStyles {
  a: string;
  strong: string;
  sup: string;
  supLink: string;
}

interface SafeHref {
  href: string;
  external: boolean;
}

function resolveSafeHref(url: string): SafeHref | null {
  if (url.startsWith("/") && !url.startsWith("//")) {
    return { href: url, external: false };
  }

  try {
    const parsed = new URL(url);
    if (parsed.protocol === "http:" || parsed.protocol === "https:") {
      return { href: url, external: true };
    }
  } catch {
    return null;
  }
  return null;
}

export function stripInlineMarkdown(line: string): string {
  return line
    .replace(INLINE_LINK_PATTERN, "$1")
    .replace(INLINE_BOLD_PATTERN, "$1")
    .replace(INLINE_SOURCE_REF_PATTERN, "");
}

export function extractSourceReferenceNumbers(markdown: string): number[] {
  const numbers: number[] = [];
  INLINE_SOURCE_REF_PATTERN.lastIndex = 0;
  let match: RegExpExecArray | null;
  while ((match = INLINE_SOURCE_REF_PATTERN.exec(markdown)) !== null) {
    numbers.push(Number(match[1]));
  }
  return numbers;
}

export type TextDirectiveRenderer = (
  attributes: Record<string, string>,
  key: string,
) => React.ReactNode;

export function parseInlineMarkdown(
  line: string,
  styles: InlineMarkdownStyles,
  textDirectives: Record<string, TextDirectiveRenderer> = {},
): React.ReactNode[] {
  const nodes: React.ReactNode[] = [];
  let lastIndex = 0;
  for (const match of findTextDirectives(line)) {
    const renderer = textDirectives[match.directive.name];
    if (renderer === undefined) {
      continue;
    }
    nodes.push(
      ...parseInlineSegment(line.slice(lastIndex, match.index), styles),
      renderer(match.directive.attributes, `inline-directive-${match.index}`),
    );
    lastIndex = match.index + match.length;
  }
  nodes.push(...parseInlineSegment(line.slice(lastIndex), styles));
  return nodes;
}

function parseInlineSegment(
  line: string,
  styles: InlineMarkdownStyles,
): React.ReactNode[] {
  const nodes: React.ReactNode[] = [];
  let lastIndex = 0;
  let key = 0;

  INLINE_PATTERN.lastIndex = 0;
  let match: RegExpExecArray | null;
  while ((match = INLINE_PATTERN.exec(line)) !== null) {
    const [full, linkText, url, boldText, sourceRefNumber] = match;

    if (match.index > lastIndex) {
      nodes.push(line.slice(lastIndex, match.index));
    }

    if (boldText !== undefined) {
      nodes.push(
        <strong key={`inline-strong-${key++}`} className={styles.strong}>
          {boldText}
        </strong>,
      );
    } else if (sourceRefNumber !== undefined) {
      nodes.push(
        <sup key={`inline-source-ref-${key++}`} className={styles.sup}>
          <a href={`#source-${sourceRefNumber}`} className={styles.supLink}>
            [{sourceRefNumber}]
          </a>
        </sup>,
      );
    } else {
      const safeHref = resolveSafeHref(url);
      if (safeHref === null) {
        nodes.push(full);
      } else {
        nodes.push(
          <a
            key={`inline-link-${key++}`}
            href={safeHref.href}
            className={styles.a}
            target={safeHref.external ? "_blank" : undefined}
            rel={safeHref.external ? "noopener noreferrer" : undefined}
          >
            {linkText}
          </a>,
        );
      }
    }

    lastIndex = match.index + full.length;
  }

  if (lastIndex < line.length) {
    nodes.push(line.slice(lastIndex));
  }

  return nodes;
}
